import { EnumListType, EnumWidgetType, ListByIdQuery } from "gql/generated";
import { OpenSelectHandGesture, RefreshDouble } from "iconoir-react";
import { useTranslation } from "react-i18next";
import {
  EmptyState,
  ListOperatorCard,
  SplitViewModal,
} from "shared/components";
import { FilterSidebar } from "./FilterSidebar";
import { Center, Flex } from "styled-system/jsx";
import { useState } from "react";
import { ManualListTabs } from "./ManualListTabs";
import { useBuildNewListModal } from "./useBuildNewListModal";
import { Spinner, Text } from "@ttc3k/trekker";
import _ from "lodash";
import { getMultilingualString } from "shared/utils";
import { BuildListContextProvider } from "./BuildListContext";
import { InternalRefetchQueriesInclude } from "@apollo/client";

type BuildNewListModalProps = {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  type: EnumListType;
  defaultList?: ListByIdQuery["listById"];
  widgetName?: string;
  updateMutationRefetchQueries?: InternalRefetchQueriesInclude;
  onWidgetCreate?: (
    widgetType: EnumWidgetType,
    name: string,
    listId: string,
  ) => void;
  onSaveComplete?: () => void;
};

export const BuildNewListModal = ({
  isOpen,
  onClose,
  type,
  name,
  defaultList,
  widgetName,
  updateMutationRefetchQueries = [],
  onWidgetCreate,
  onSaveComplete,
}: BuildNewListModalProps) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"results" | "selections">(
    defaultList ? "selections" : "results",
  );

  const {
    appliedFilters,
    onUpdateAppliedFilters,
    operators,
    operatorsError,
    operatorsLoading,
    hasActiveFiltersApplied,
    onResetAppliedFilters,
    listCreateLoading,
    listUpdateLoading,
    onListSave,
    onUpdateSearch,
    search,
    onUpdateSelectedOperators,
    selectedOperators,
    onResetSelectedOperators,
    tagsError,
    tagsLoading,
    allTagsForOrganization,
  } = useBuildNewListModal(
    name,
    type,
    defaultList,
    widgetName,
    updateMutationRefetchQueries,
    onWidgetCreate,
    onSaveComplete,
  );

  if (operatorsError) {
    throw new Error("Error loading the operators while building a list");
  }

  const handleClose = () => {
    // Reset all states to the default
    onResetAppliedFilters();
    onUpdateSearch("");
    onResetSelectedOperators();
    setTab(defaultList ? "selections" : "results");

    // Close the modal
    onClose();
  };

  const handleSaveClick = () => {
    onListSave();
    handleClose();
  };

  const isViewingMySelections =
    type === EnumListType.Manual && tab === "selections";
  const isSmartList = type === EnumListType.Smart;

  // Display selected operator when on "My Selections" tab, otherwise display
  // the operators queried
  const operatorsToDisplay = isViewingMySelections
    ? selectedOperators
    : operators;

  return (
    <BuildListContextProvider
      tags={allTagsForOrganization}
      tagsError={tagsError}
      tagsLoading={tagsLoading}
    >
      <SplitViewModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          defaultList
            ? t("customLists:LIST.UPDATE_LIST", {
                listName: getMultilingualString(defaultList.name),
              })
            : t(`customLists:LIST.CREATE_NEW.${type}.CREATE`)
        }
        HeaderIcon={isSmartList ? RefreshDouble : OpenSelectHandGesture}
        saveButtonProps={{
          children: "Save list",
          onClick: handleSaveClick,
          isLoading: listCreateLoading || listUpdateLoading,
          // If SMART list - disabled save if no filters applied
          // if MANUAL list - disable save if no operators are selected
          disabled: isSmartList
            ? !hasActiveFiltersApplied
            : selectedOperators.length < 1,
        }}
        sidebar={
          <FilterSidebar
            type={type}
            appliedFilters={appliedFilters}
            onUpdateAppliedFilters={onUpdateAppliedFilters}
            search={search}
            onUpdateSearch={onUpdateSearch}
          />
        }
      >
        <Flex flexDir={"column"} gap={"400"}>
          {!isSmartList && (
            <ManualListTabs
              tab={tab}
              onTabChange={(v) => setTab(v)}
              searchResultsCount={
                hasActiveFiltersApplied && operators ? operators.length : 0
              }
              mySelectionsCount={selectedOperators.length}
            />
          )}
          {hasActiveFiltersApplied || isViewingMySelections ? (
            <>
              {operatorsLoading ? (
                <Center h={"full"}>
                  <Spinner w={"40px"} h={"40px"} />
                </Center>
              ) : (
                <>
                  {operatorsToDisplay && operatorsToDisplay.length > 0 ? (
                    <Flex flexDir={"column"} gap={"150"} width={"full"}>
                      {isSmartList && (
                        <Text
                          visual={"smallSemiBold"}
                          color={"text.mid"}
                          width={"full"}
                          textAlign={"right"}
                        >
                          {t("customLists:LIST.CREATE_NEW.RESULTS", {
                            count: operators?.length ?? 0,
                          })}
                        </Text>
                      )}
                      {_.sortBy(operatorsToDisplay, (op) => {
                        const name = getMultilingualString(
                          op.name,
                        ).toLowerCase();
                        // When sorting, add prefix of zzz to put names that
                        // start with a number at the bottom of the list
                        return /^\d/.test(name) ? `zzz${name}` : name;
                      }).map((operator) => {
                        const isOperatorSelected = !!selectedOperators.find(
                          (op) => op._versionId === operator._versionId,
                        );
                        return (
                          <ListOperatorCard
                            key={operator._versionId}
                            operator={operator}
                            listType={type}
                            isAdded={isOperatorSelected}
                            onAddOperator={() =>
                              onUpdateSelectedOperators(operator)
                            }
                          />
                        );
                      })}
                    </Flex>
                  ) : (
                    <EmptyState
                      text={t(`customLists:LIST.CREATE_NEW.${type}.EMPTY`)}
                      w={"full"}
                      minH={"604px"}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <EmptyState
              text={t(`customLists:LIST.CREATE_NEW.${type}.EMPTY`)}
              w={"full"}
              minH={"604px"}
            />
          )}
        </Flex>
      </SplitViewModal>
    </BuildListContextProvider>
  );
};
